import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Plus } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { API, CAN_WRITE_ROLES, extractErr } from '../components/kb/utils';
import { KBCard } from '../components/kb/tenant/KBCard';
import { NewKBModal } from '../components/kb/tenant/NewKBModal';

const KnowledgeBasesPage = () => {
  const { user, token } = useAuth();
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  // Whether the current user can write. Also gated server-side.
  const canWrite = useMemo(() => CAN_WRITE_ROLES.has(user?.role), [user]);

  const fetchKnowledgeBases = useCallback(async () => {
    setLoading(true);
    try {
      // Backend already scopes to this user's domain AND filters by the
      // domain's industry + extension_classifications. NO client-side
      // filtering — the old `filter(kb => kb.call_type === userIndustry)`
      // was silently hiding ALL 6 TotalFusion KBs because their call_types
      // are like 'manager_leadership' / 'sales_new' rather than the
      // industry slug 'fitness_gym'.
      const response = await axios.get(`${API}/knowledge-bases`);
      setKnowledgeBases(response.data || []);
    } catch (err) {
      console.error('Failed to load KBs', err?.response || err);
      toast.error(extractErr(err, 'Failed to load knowledge bases'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKnowledgeBases(); }, [fetchKnowledgeBases]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-bases-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            Knowledge Bases
          </h1>
          <p className="text-slate-600">
            Evaluation criteria for different call types. Uploaded documents are the source-of-truth; preset criteria are the fallback when no document exists.
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowNew(true)} data-testid="new-kb-btn">
            <Plus className="w-4 h-4 mr-1" /> New KB
          </Button>
        )}
      </div>

      <div className="grid gap-6">
        {knowledgeBases.map((kb) => (
          <KBCard key={kb.id} kb={kb} canWrite={canWrite} token={token} onChanged={fetchKnowledgeBases} />
        ))}

        {knowledgeBases.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No knowledge bases found</h3>
              <p className="text-slate-500">
                {canWrite
                  ? 'Create your first knowledge base to start grading calls.'
                  : 'Your admin has not set up any knowledge bases yet.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {showNew && (
        <NewKBModal
          onClose={() => setShowNew(false)}
          onCreated={fetchKnowledgeBases}
          domainCallType=""
        />
      )}
    </div>
  );
};

export default KnowledgeBasesPage;
